<template>
  <div class="cart-wishlist">
    <FormSelect
      v-model="selectSizeId"
      class="cart-wishlist__select"
      :placeholder="selectTitle"
      :values="selectValues"
      :disabled="disabled"
      max-height="120px"
      :has-error="selectError"
      @select="handleSelect"
    />

    <UIButton
      size="xs"
      type="default"
      class="cart-wishlist__send"
      :loading="loading"
      @click="addCart"
    >
      {{ $t('components.catalog.productListItemAdd.addCart') }}
    </UIButton>
  </div>
</template>

<script setup lang="ts">
import { HTTPError } from 'ky';
import { SizeValueDTOReq } from '~/restAPI/data-contracts';
import { useProductService } from '~/services/product';

const props = defineProps<{
  sizes?: SizeValueDTOReq[]
  productId: number
}>();

const selectSizeId = ref<number>();
const selectError = ref(false);
const loading = ref(false);

const { t } = useI18n();
const { updateCart } = useCartStore();
const { addProductToCart } = useProductService();

const selectTitle = computed(() => {
  if (!props.sizes) {
    return t('components.catalog.productListItemAdd.withoutSize');
  }
  if (props.sizes.length === 1) {
    return getTitle(props.sizes[0]);
  }

  return t('components.catalog.productListItemAdd.selectSize');
});
const disabled = computed(() => !props.sizes || props.sizes.length === 1);
const selectValues = computed(() => props.sizes?.map((size) => ({
  id: size.id,
  name: getTitle(size),
})) ?? []);

function getTitle(size: SizeValueDTOReq) {
  return `${size.productSizeType !== 'NO_SIZE' ? size.productSizeType : ''} ${size.productSizeValue}`;
}

function handleSelect() {
  selectError.value = false;
}

async function addCart() {
  if (!disabled.value && !selectSizeId.value) {
    selectError.value = true;
    return;
  }

  selectError.value = false;
  loading.value = true;

  try {
    const sizeId = selectSizeId.value ?? props.sizes?.[0].id;
    const data = await addProductToCart({ productId: props.productId, sizeId });

    updateCart(data);
  } catch (e) {
    if (e instanceof HTTPError) {
      const messageError = await e.response.json();

      window.alert(messageError.humanMessage);
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped lang="scss">
.cart-wishlist {
  width: 100%;
  margin-top: 24px;

  &__select {
    text-align: center;

    :deep(.select__value) {
      padding-left: 0;
      padding-right: 0;
    }
  }

  &__send {
    margin-top: 12px;
    background: hsla(0,0%,95%,.4);
    height: 38px;
    width: 100%;
    line-height: 17px;
    font-weight: 600;
  }
}
</style>
